/**
 * Color Role - Validate Pillar
 *
 * Validação de valores de cor.
 * ZERO dependências de outros pilares - totalmente standalone.
 *
 * @example
 * import { validateColor, isValidColor } from '@attrx/role-morphic/color/validate';
 *
 * validateColor('hex', '#ff0000');           // { valid: true, errors: [] }
 * validateColor('rgb_object', { r: 255, g: 0, b: 0 }); // { valid: true, errors: [] }
 * validateColor('hex', 'invalid');           // { valid: false, errors: [...] }
 */

import type { ColorVariant, RGBA, ColorRgbObject, ColorHslObject } from "./types";

// =============================================================================
// TYPES
// =============================================================================

export type ValidationResult = { valid: boolean; errors: string[] };

// =============================================================================
// REGEX PATTERNS
// =============================================================================

const HEX_3_REGEX = /^#?[0-9a-f]{3}$/i;
const HEX_4_REGEX = /^#?[0-9a-f]{4}$/i;
const HEX_6_REGEX = /^#?[0-9a-f]{6}$/i;
const HEX_8_REGEX = /^#?[0-9a-f]{8}$/i;
const RGB_STRING_REGEX = /^rgba?\s*\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/i;
const HSL_STRING_REGEX = /^hsla?\s*\(\s*[\d.]+\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?\s*(,\s*[\d.]+\s*)?\)$/i;

// =============================================================================
// VALIDATE BY VARIANT
// =============================================================================

/**
 * Valida valor hex
 */
export function validateHex(value: unknown): ValidationResult {
  const errors: string[] = [];

  if (typeof value !== "string") {
    errors.push("Hex color must be a string");
    return { valid: false, errors };
  }

  const trimmed = value.trim();

  if (
    !HEX_3_REGEX.test(trimmed) &&
    !HEX_4_REGEX.test(trimmed) &&
    !HEX_6_REGEX.test(trimmed) &&
    !HEX_8_REGEX.test(trimmed)
  ) {
    errors.push("Invalid hex color format");
    return { valid: false, errors };
  }

  return { valid: true, errors: [] };
}

/**
 * Valida valor RGB object
 */
export function validateRgbObject(value: unknown): ValidationResult {
  const errors: string[] = [];

  if (typeof value !== "object" || value === null) {
    errors.push("RGB object must be an object");
    return { valid: false, errors };
  }

  const obj = value as Record<string, unknown>;

  // Verifica propriedades obrigatórias
  if (typeof obj.r !== "number") {
    errors.push("RGB object must have numeric 'r' property");
  }
  if (typeof obj.g !== "number") {
    errors.push("RGB object must have numeric 'g' property");
  }
  if (typeof obj.b !== "number") {
    errors.push("RGB object must have numeric 'b' property");
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  const { r, g, b, a } = obj as unknown as ColorRgbObject;

  // Valida ranges
  if (r < 0 || r > 255) {
    errors.push("RGB 'r' must be between 0 and 255");
  }
  if (g < 0 || g > 255) {
    errors.push("RGB 'g' must be between 0 and 255");
  }
  if (b < 0 || b > 255) {
    errors.push("RGB 'b' must be between 0 and 255");
  }
  if (a !== undefined && (typeof a !== "number" || a < 0 || a > 1)) {
    errors.push("RGB 'a' must be a number between 0 and 1");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Valida valor RGB string
 */
export function validateRgbString(value: unknown): ValidationResult {
  const errors: string[] = [];

  if (typeof value !== "string") {
    errors.push("RGB string must be a string");
    return { valid: false, errors };
  }

  const trimmed = value.trim();

  if (!RGB_STRING_REGEX.test(trimmed)) {
    errors.push("Invalid RGB string format");
    return { valid: false, errors };
  }

  return { valid: true, errors: [] };
}

/**
 * Valida valor HSL object
 */
export function validateHslObject(value: unknown): ValidationResult {
  const errors: string[] = [];

  if (typeof value !== "object" || value === null) {
    errors.push("HSL object must be an object");
    return { valid: false, errors };
  }

  const obj = value as Record<string, unknown>;

  // Verifica propriedades obrigatórias
  if (typeof obj.h !== "number") {
    errors.push("HSL object must have numeric 'h' property");
  }
  if (typeof obj.s !== "number") {
    errors.push("HSL object must have numeric 's' property");
  }
  if (typeof obj.l !== "number") {
    errors.push("HSL object must have numeric 'l' property");
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  const { h, s, l, a } = obj as unknown as ColorHslObject;

  // Valida ranges
  if (h < 0 || h > 360) {
    errors.push("HSL 'h' must be between 0 and 360");
  }
  if (s < 0 || s > 100) {
    errors.push("HSL 's' must be between 0 and 100");
  }
  if (l < 0 || l > 100) {
    errors.push("HSL 'l' must be between 0 and 100");
  }
  if (a !== undefined && (typeof a !== "number" || a < 0 || a > 1)) {
    errors.push("HSL 'a' must be a number between 0 and 1");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Valida valor HSL string
 */
export function validateHslString(value: unknown): ValidationResult {
  const errors: string[] = [];

  if (typeof value !== "string") {
    errors.push("HSL string must be a string");
    return { valid: false, errors };
  }

  const trimmed = value.trim();

  if (!HSL_STRING_REGEX.test(trimmed)) {
    errors.push("Invalid HSL string format");
    return { valid: false, errors };
  }

  return { valid: true, errors: [] };
}

// =============================================================================
// MAIN VALIDATE
// =============================================================================

/**
 * Valida valor de cor para uma variante específica
 *
 * @param variant - Variante a validar
 * @param value - Valor a validar
 * @returns Resultado da validação
 *
 * @example
 * validateColor('hex', '#ff0000');
 * // { valid: true, errors: [] }
 *
 * validateColor('hex', 'invalid');
 * // { valid: false, errors: ['Invalid hex color format'] }
 */
export function validateColor(variant: ColorVariant, value: unknown): ValidationResult {
  switch (variant) {
    case "hex":
      return validateHex(value);
    case "rgb_object":
      return validateRgbObject(value);
    case "rgb_string":
      return validateRgbString(value);
    case "hsl_object":
      return validateHslObject(value);
    case "hsl_string":
      return validateHslString(value);
    default:
      return { valid: false, errors: [`Unknown color variant: ${variant}`] };
  }
}

/**
 * Verifica se valor é válido para uma variante específica (retorna boolean)
 *
 * @example
 * isValidColorVariant('hex', '#ff0000');  // true
 * isValidColorVariant('hex', 'invalid');  // false
 */
export function isValidColorVariant(variant: ColorVariant, value: unknown): boolean {
  return validateColor(variant, value).valid;
}

/**
 * Verifica se variante existe
 */
export function hasColorVariant(variant: string): variant is ColorVariant {
  return (
    variant === "hex" ||
    variant === "rgb_object" ||
    variant === "rgb_string" ||
    variant === "hsl_object" ||
    variant === "hsl_string"
  );
}

/**
 * Lista de variantes disponíveis
 */
export const COLOR_VARIANTS: ColorVariant[] = [
  "hex",
  "rgb_object",
  "rgb_string",
  "hsl_object",
  "hsl_string",
];
